// THE `report` PAYLOAD — the deliverable of a run that does not open a PR, plus the
// artifact REFERENCES it names (never inlined bytes).
// Split out of `../task-run-event.ts`, which is now the barrel over this directory.

import { z } from 'zod';

/**
 * One artifact a non-PR run produced — a REFERENCE, never inlined bytes. `ref`
 * is a box path or (once the artifact store lands, doc 08) a storage URI; the
 * event row stays O(metadata) so the audit chain doesn't carry blobs.
 */
export const ReportArtifactSchema = z.object({
  /** Display label — the file name or a title. */
  name: z.string().min(1).max(200),
  /** Where the bytes live — a box path or an artifact-store URI (doc 08). Reference only. */
  ref: z.string().min(1).max(500),
  /** Optional MIME type, when the run knows it. */
  media_type: z.string().min(1).max(100).optional(),
  /** Optional size in bytes. */
  bytes: z.number().int().nonnegative().optional(),
});
export type ReportArtifact = z.infer<typeof ReportArtifactSchema>;

/**
 * The `report` event payload (doc 04) — the deliverable of a `report` /
 * `ops_action` task that DOESN'T open a PR. Carries the markdown body plus
 * artifact references; the babysit watch is skipped, so this event (persisted
 * in `task_run_events`, audited, rendered in the run view) IS the outcome.
 * `outcome_kind` is the two non-PR kinds only — a `pr` task never emits this.
 * `markdown` is bounded (the row is jsonb; the audit chain stays lean).
 */
export const ReportPayloadSchema = z.object({
  /** Which non-PR outcome produced this — matches `OutcomeKind` `report` / `ops_action`. */
  outcome_kind: z.enum(['report', 'ops_action']),
  /** The markdown report body the run delivered. */
  markdown: z.string().max(100_000),
  /** Artifact references (doc 08 store lands later; these are references, never inlined bytes). */
  artifacts: z.array(ReportArtifactSchema).max(50).default([]),
});
export type ReportPayload = z.infer<typeof ReportPayloadSchema>;
